/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Filter } from '../../../model/Filter';
import { BaseController } from '../../BaseController';

class PreventivePlanningController extends BaseController {
    /** @ngInject */
    constructor(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log,
        preventiveSheetService, domainService, siteService, filterService, dateTimeService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.preventiveSheetService = preventiveSheetService;
        this.domainService = domainService;
        this.siteService = siteService;
        this.filterService = filterService;
        this.dateTimeService = dateTimeService;
        this.$log.debug('Controller \'PreventivePlanning\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.filterDomains = [];
        this.filterSites = [];
        this.model = {};
        this.sort = 'maxActionDate';
        this.currentMonth = new Date();
        // Using the first day of month prevents issues when switching to next/previous months, as months don't have the
        // same number of days.
        this.currentMonth.setUTCDate(1);

        this.domainFilter = Filter.equalFilterEmpty('equipment.site.domain.id', 'model.domain.domain', 'SELECT',
            this.filterDomains, domainId => this.getFilterSites(domainId));
        this.siteFilter = Filter.equalFilterEmpty('equipment.site.id', 'model.site.site', 'SELECT_AUTOCOMPLETE', this.filterSites);
        this.firstDayFilter = Filter.greaterOrEqualDateFilter('maxActionDate', undefined,
            'ui.preventivesheet.plannedfrom', filter => this.toLocalDate(filter), true);
        this.lastDayFilter = Filter.lowerOrEqualDateFilter('maxActionDate', undefined,
            'ui.preventivesheet.plannedto', filter => this.toLocalDate(filter), true);
        this.filters = [this.domainFilter, this.siteFilter, this.firstDayFilter, this.lastDayFilter];
        this.toMonth(0);

        this.commands = [];
        this.handleRestServiceCall(this.loadFilters(),
            () => {
                this.initCommands();
                this.reload();
            }
        ).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        // No commands
    }

    /**
     * Loads data in filters.
     */
    loadFilters() {
        return this.getFilterDomains()
            .then(() => this.getFilterSites(this.domainFilter.value));
    }

    /**
     * Gets the list of domains.
     *
     * @return {Object[]} Promise providing an array of domains.
     */
    getFilterDomains() {
        return this.filterService.getFilterOptions(this.domainService, this.domainFilter, 'name');
    }

    /**
     * Gets the list of sites for a given domain.
     *
     * @param {Number} [domainId] Domain ID, may be undefined if no domain is selected.
     * @return {Object[]} Promise providing an array of sites.
     */
    getFilterSites(domainId) {
        return this.filterService.getOrClearFilterOptions(this.siteService, this.siteFilter, 'name',
            [Filter.equalFilter('domain.id', domainId)], domainId);
    }

    /**
     * Gets a text representation of the month currently selected in the planning.
     *
     * @return {String} Month.
     */
    getPlanningMonth() {
        return this.dateTimeService.formatLocalDate(this.currentMonth, 'MMMM GGGG');
    }

    /**
     * Transforms a date filter to a local date value, before serialization in an HTTP request.
     *
     * @param {Filter} filter Filter.
     */
    toLocalDate(filter) {
        filter.value = this.dateTimeService.formatLocalDate(filter.value);
    }

    /**
     * Sets the planning range to another month (past or future). Example:
     *
     * <pre>
     * toMonth(-1) // Show the planning for the previous month.
     * toMonth(0) // Show the planning for the current month.
     * toMonth(2) // Show the planning for the month two steps ahead.
     * </pre>
     *
     * @param {Number} numberOfMonths Number of months to go backward or forward.
     */
    toMonth(numberOfMonths) {
        this.currentMonth.setUTCMonth(this.currentMonth.getUTCMonth() + numberOfMonths);
        this.firstDayFilter.value = this.dateTimeService.getFirstDayOfFirstWeekCurrentMonth(this.currentMonth);
        this.lastDayFilter.value = this.dateTimeService.getFirstDayOfFirstPlainWeekNextMonth(this.currentMonth);
    }

    /**
     * Switches the view to another month (past of future). This method reloads automatically the data in the planning.
     *
     * @param {Number} numberOfMonths Number of months to go backward or forward.
     * @return {Promise} A promise providing the list of entities.
     */
    switchToMonth(numberOfMonths) {
        this.toMonth(numberOfMonths);
        return this.reload();
    }

    /**
     * Reloads the list of entities.
     *
     * @return {Promise} A promise providing the list of entities.
     */
    reload() {
        return this.handleRestServiceCall(
            this.preventiveSheetService.findLightEntities({
                params: this.filterService.buildSortingParam({sort: this.sort}),
                data: this.filterService.buildFilters(this.filters)
            }),
            data => {
                this.initWeeks();
                this.model.preventiveSheets = {};

                // Sort preventive sheets by their planned week. The final model is an object whose keys are week
                // numbers as 'YYYY-SWW' string, and an array of preventive sheets that are planned for each week.
                data.forEach(preventiveSheet => {
                    // Compute the planned week as a 'YYYY-SWW' string.
                    preventiveSheet.plannedWeek = this.dateTimeService.toPreventiveSheetPlannedWeek(
                        preventiveSheet.maxActionDate);

                    if (angular.isUndefined(this.model.preventiveSheets[preventiveSheet.plannedWeek])) {
                        this.model.preventiveSheets[preventiveSheet.plannedWeek] = [];
                    }

                    this.model.preventiveSheets[preventiveSheet.plannedWeek].push(preventiveSheet);
                });
            }
        );
    }

    /**
     * Initializes the weeks that will be show in the planning, based on the filters' value.
     */
    initWeeks() {
        this.model.weeks = [];
        // First week to be dealt with is the week of the first day filter.
        let currentWeek = this.dateTimeService.formatLocalDate(this.firstDayFilter.value,
            this.configuration.WEEK_OF_YEAR_FORMAT);
        const lastWeek = this.dateTimeService.formatLocalDate(this.lastDayFilter.value,
            this.configuration.WEEK_OF_YEAR_FORMAT);

        while (currentWeek !== lastWeek) {
            this.model.weeks.push(currentWeek);
            currentWeek = this.dateTimeService.incrementIsoweek(currentWeek,
                this.configuration.WEEK_OF_YEAR_FORMAT);
        }
    }
}

export const preventivePlanningComponent = {
    template: require('./preventiveplanning.html'),
    controller: PreventivePlanningController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/prevention/component/PreventivePlanning.js